import {
  ArrowForward,
  Cancel,
  CheckCircle,
  Edit,
  OpenInNew,
} from '@mui/icons-material';
import {
  Box,
  Card,
  CardContent,
  Chip,
  IconButton,
  Tooltip,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';
import { Branch } from 'common/model/Branch';

type BranchCardProps = {
  branch: Branch;
  onSelect: (objectId: string) => void;
  onOpenOrderSite: (objectId: string, e: React.MouseEvent) => void;
  onOpenManagement: (objectId: string, e: React.MouseEvent) => void;
  onEdit: (branch: Branch, e: React.MouseEvent) => void;
};

export const BranchCard = ({
  branch,
  onSelect,
  onOpenOrderSite,
  onOpenManagement,
  onEdit,
}: BranchCardProps) => {
  const theme = useTheme();

  return (
    <Card
      sx={{
        height: '100%',
        cursor: 'pointer',
        transition: 'all 0.2s ease',
        border: `1px solid ${alpha(theme.palette.divider, 0.12)}`,
        borderRadius: 2,
        background: theme.palette.background.default,
        boxShadow: `0 1px 3px ${alpha(theme.palette.common.black, 0.08)}`,
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        '&:hover': {
          borderColor: theme.palette.primary.main,
          boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.15)}`,
          '& .arrow-icon': {
            transform: 'translateX(2px)',
          },
        },
      }}
      onClick={() => onSelect(branch.objectId)}
    >
      <Chip
        icon={
          branch.enabled ? (
            <CheckCircle sx={{ fontSize: 14 }} />
          ) : (
            <Cancel sx={{ fontSize: 14 }} />
          )
        }
        label={branch.enabled ? 'Aktif' : 'Pasif'}
        size="small"
        sx={{
          position: 'absolute',
          top: 8,
          right: 8,
          zIndex: 1,
          height: 24,
          backgroundColor: branch.enabled
            ? alpha(theme.palette.success.main, 0.1)
            : alpha(theme.palette.error.main, 0.1),
          color: branch.enabled
            ? theme.palette.success.main
            : theme.palette.error.main,
          fontWeight: 600,
          fontSize: '0.7rem',
          border: `1px solid ${alpha(
            branch.enabled
              ? theme.palette.success.main
              : theme.palette.error.main,
            0.2,
          )}`,
        }}
      />
      <CardContent
        sx={{
          p: 2,
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          pb: '5px!important',
        }}
      >
        <Box sx={{ mb: 1 }}>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 600,
              color: theme.palette.text.primary,
              fontSize: '1rem',
              mb: 0.5,
            }}
          >
            {branch.name || 'Şube'}
          </Typography>
          <Typography
            variant="caption"
            sx={{
              color: theme.palette.text.secondary,
              fontSize: '0.7rem',
              fontFamily: 'monospace',
              fontWeight: 500,
            }}
          >
            ID: {branch.objectId}
          </Typography>
        </Box>

        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1,
            pt: '5px!important',
            mt: 'auto',
            borderTop: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-start',
            }}
          >
            <Tooltip title="Sipariş Sitesini Aç">
              <IconButton
                onClick={(e) => onOpenOrderSite(branch.objectId, e)}
                sx={{
                  color: theme.palette.primary.main,
                  '&:hover': {
                    backgroundColor: alpha(theme.palette.primary.main, 0.1),
                  },
                }}
              >
                <OpenInNew sx={{ fontSize: 20 }} />
              </IconButton>
            </Tooltip>
            <Tooltip title="Düzenle">
              <IconButton
                onClick={(e) => onEdit(branch, e)}
                sx={{
                  color: theme.palette.primary.main,
                  '&:hover': {
                    backgroundColor: alpha(theme.palette.info.main, 0.1),
                  },
                }}
              >
                <Edit sx={{ fontSize: 18 }} />
              </IconButton>
            </Tooltip>
          </Box>
          <Box
            className="arrow-icon"
            sx={{
              display: 'flex',
              alignItems: 'center',
              color: theme.palette.text.secondary,
              transition: 'all 0.2s ease',
              cursor: 'pointer',
              '&:hover': {
                color: theme.palette.primary.main,
              },
            }}
            onClick={() => onSelect(branch.objectId)}
          >
            <Typography
              variant="body2"
              sx={{
                fontWeight: 600,
                mr: 0.5,
                fontSize: '0.8rem',
              }}
            >
              Seç
            </Typography>
            <ArrowForward sx={{ fontSize: 16 }} />
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};
